/**
 * PrintRoot — the print root of the pharmacy (refonte WP-19 · F4, design/ux/01_foundations.md
 * §3.16, AUD-UXDS-10).
 *
 * Wraps ONE printable document (a PrintDocument, a LabelSheet) and prints it from a hidden iframe
 * holding only that document, the print stylesheet and its `@page` rule — never the app shell.
 *
 *   const printRef = React.useRef<PrintRootHandle>(null);
 *   <PrintRoot ref={printRef} preview>            // preview: the paper is shown on screen
 *     <PrintDocument … />
 *   </PrintRoot>
 *   printRef.current?.print();                    // → "printed" | "busy" | "failed"
 *
 * Without `preview` the document is rendered in a hidden host (serialisable, never seen, out of the
 * tab order and the accessibility tree). For a screen that prints without any preview, use
 * `printElement(<PrintDocument … />)`, which renders the document off-DOM only for the print.
 * Both share one guard (one print at a time, « Préparation de l'impression… » when slow, the
 * failure message M-S3-23 as an error toast) and put « Imprimé le … par <user> » on A4 pages.
 * Ctrl+P is bound by the screen through `useCtrlP` (hooks/useCtrlP.js).
 */
import * as React from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { cn } from "@/lib/utils";
import PRINT_CSS from "@/styles/print.css?inline";
import { createPrintRunner, printDocNode } from "@/lib/print-frame.js";
import { PRINT_MESSAGES, PRINT_LABELS } from "@/config/print.config.js";
import { notifyToast } from "@/hooks/useShortcuts.js";
import { useAuthStore } from "@/stores/authStore.js";

export type PrintOutcome = "printed" | "busy" | "failed";

export interface PrintRootHandle {
  /** Print the wrapped document. */
  print: () => Promise<PrintOutcome>;
  /** The rendered `[data-print-doc]` node (tests, previews). */
  documentNode: () => Element | null;
  /** True while a print is being prepared. */
  isPrinting: () => boolean;
}

export interface PrintRootProps {
  children: React.ReactNode;
  /** Show the paper on screen (document preview page, label dialog). */
  preview?: boolean;
  /** Accessible name of the preview region (« Aperçu de la facture »). */
  label?: string;
  /** "etiquettes" picks the label-printer failure message (M-S3-23). */
  kind?: "document" | "etiquettes";
  /** The user named in the footer (defaults to the signed-in user). */
  printedBy?: string;
  onPrintingChange?: (printing: boolean) => void;
  className?: string;
}

/** « Prénom Nom » of the signed-in user (the A4 footer). */
export function currentUserName(): string {
  const u = (useAuthStore as any).getState?.()?.user;
  return [u?.prenom, u?.nom].filter(Boolean).join(" ").trim();
}

const runners = new Map<string, ReturnType<typeof createPrintRunner>>();

/** One guard per kind for the whole app: two print roots never open two dialogs at once. */
function runnerFor(kind: "document" | "etiquettes") {
  let r = runners.get(kind);
  if (!r) {
    r = createPrintRunner({
      notify: notifyToast,
      messages: {
        preparation: PRINT_MESSAGES.preparation,
        echec: kind === "etiquettes" ? PRINT_MESSAGES.echecEtiquettes : PRINT_MESSAGES.echecDocument,
        enCours: PRINT_MESSAGES.enCours,
      },
    });
    runners.set(kind, r);
  }
  return r;
}

export const PrintRoot = React.forwardRef<PrintRootHandle, PrintRootProps>(
  ({ children, preview = false, label, kind = "document", printedBy, onPrintingChange, className }, ref) => {
    const hostRef = React.useRef<HTMLDivElement>(null);
    const [printing, setPrinting] = React.useState(false);
    const changeRef = React.useRef(onPrintingChange);
    changeRef.current = onPrintingChange;

    const documentNode = React.useCallback(
      () => hostRef.current?.querySelector("[data-print-doc]") ?? null,
      [],
    );

    const print = React.useCallback(async (): Promise<PrintOutcome> => {
      const runner = runnerFor(kind);
      if (!runner.busy) {
        setPrinting(true);
        changeRef.current?.(true);
      }
      const out = (await runner.run(() =>
        printDocNode(documentNode() as Element, {
          css: PRINT_CSS,
          printedBy: printedBy ?? currentUserName(),
        }),
      )) as PrintOutcome;
      if (out !== "busy") {
        setPrinting(false);
        changeRef.current?.(false);
      }
      return out;
    }, [kind, printedBy, documentNode]);

    React.useImperativeHandle(ref, () => ({ print, documentNode, isPrinting: () => runnerFor(kind).busy }), [
      print,
      documentNode,
      kind,
    ]);

    if (preview) {
      return (
        <div
          ref={hostRef}
          className={cn("pk-print-stage", className)}
          role="region"
          aria-label={label ?? PRINT_LABELS.apercu}
          aria-busy={printing || undefined}
          data-print-host=""
        >
          {children}
        </div>
      );
    }
    return (
      <div
        ref={hostRef}
        className={cn("pk-print-host--hidden", className)}
        aria-hidden="true"
        // React 18 has no typed `inert`; the attribute keeps the hidden copy out of the tab order.
        {...({ inert: "" } as any)}
        data-print-host=""
      >
        {children}
      </div>
    );
  },
);
PrintRoot.displayName = "PrintRoot";

/**
 * Print a document element without mounting it in the page: rendered into a detached container,
 * printed, unmounted. For a screen whose Ctrl+P prints straight away (no preview).
 *
 * @param element a PrintDocument / LabelSheet element (it must not need the router)
 */
export async function printElement(
  element: React.ReactElement,
  opts: { kind?: "document" | "etiquettes"; printedBy?: string } = {},
): Promise<PrintOutcome> {
  const runner = runnerFor(opts.kind ?? "document");
  return (await runner.run(async () => {
    const container = document.createElement("div");
    const root = createRoot(container);
    try {
      flushSync(() => root.render(element));
      await printDocNode(container, { css: PRINT_CSS, printedBy: opts.printedBy ?? currentUserName() });
    } finally {
      // Unmount after the current task: React refuses a synchronous unmount during a render.
      setTimeout(() => root.unmount(), 0);
    }
  })) as PrintOutcome;
}

export default PrintRoot;
